import React from 'react';
import styled from 'styled-components';
import {
  Flexbox,
  ThemeButton,
  TextElement,
  Card
} from '@ibinder/ui-common-core';
import { SearchableSingleSelect } from '@ibinder/common-frontend';
import { OrgPropertyDisplay } from './components/OrgPropertyDisplay';
import { getFormatedDate, iBinderHeaderEnum } from '@ibinder/common-utils';

import { GetFlag } from './components/GetFlag';
import { useFeatureToggle, useIBTranslation } from '@ibinder/ui-common-hooks';
import { DEFAULT_PLATFORM, platformData } from '../CreateOrgForm';
import { PackageHeader } from './OrganizationPackages';
import {
  BinderUsageDisplay,
  useOrganizationBindersUsage
} from './components/BindersDisplay';
import upperFirst from 'lodash/upperFirst';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_ORG_SETTINGS,
  GET_ORGANIZATION_SUMMARY,
  UPDATE_ORG_TERMINATION_PERIODS,
  UPDATE_ORG_THRESHOLDS,
  UPDATE_ORGANIZATION_CUSTOMER
} from 'Shared/api/queries/getOrganization.query';
import { GET_ALL_CUSTOMERS } from '../../../../../../../src/api/queries/queries_local';
import { ThemeTextField } from '@ibinder/ui-common-core';
import { buttonTypes } from '@ibinder/ui-common-styles';

const StyledFlexbox = styled(Flexbox)`
  gap: 1rem;
`;
export const FlagFlexbox = styled(Flexbox)`
  > svg {
    height: 1rem;
  }
  > span {
    margin-left: 0.5rem;
  }
`;

const Row = styled(Flexbox)`
  gap: 1rem;
  width: 100%;
`;

const Column = styled(Flexbox)`
  width: 50%;
`;

const BinderRow = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;

  > :last-child {
    grid-column: 3;
  }
`;

const CustomerSelectorContainer = styled.div`
  margin-bottom: 1rem;
`;

const SUMMARY_LAYOUT = [
  // Storage metrics
  ['totalSize', 'totalDeletedSize'],
  // Document counts
  ['documentsCount', 'deletedDocumentsCount'],
  // Version counts
  ['documentVersionsCount', 'deletedVersionsCount']
];

type NumericInputValue = number | '';
type ThresholdKey = 'facility' | 'project' | 'tender';
type TerminationPeriodKey = 'facility' | 'project';

const DEFAULT_TERMINATION_PERIOD = 3;

const normalizeTerminationPeriod = (value: unknown) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && value != null && value !== ''
    ? Math.min(12, Math.max(1, Math.floor(parsedValue)))
    : DEFAULT_TERMINATION_PERIOD;
};

type OrganizationInfoProps = {
  organizationData: any;
  platform: any;
  onDirtyChange?: (dirty: boolean) => void;
};

const OrganizationInfo = ({
  organizationData,
  platform,
  onDirtyChange
}: OrganizationInfoProps) => {
  const { isEnabled: useSelectOrgPackage } = useFeatureToggle(
    'Configcat_selectPackageOnOrgCreation'
  );
  const { data: organizationSummaryData, loading: loadingOrganizationSummary } =
    useQuery(GET_ORGANIZATION_SUMMARY, {
      context: {
        headers: { [iBinderHeaderEnum.TENANTID]: organizationData?.id }
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        organizationId: organizationData?.id
      },
      skip: !organizationData?.id
    });
  const {
    data = { settings: [] },
    loading: loadingOrgSettings,
    refetch: refetchOrgSettings
  } = useQuery(GET_ORG_SETTINGS, {
    fetchPolicy: 'no-cache',
    context: {
      headers: { [iBinderHeaderEnum.TENANTID]: organizationData?.id }
    },
    skip: !organizationData?.id
  });

  const [updateOrganizationThresholds] = useMutation(UPDATE_ORG_THRESHOLDS);
  const [updateOrganizationTerminationPeriods] = useMutation(
    UPDATE_ORG_TERMINATION_PERIODS
  );
  const [updateOrganizationCustomer] = useMutation(
    UPDATE_ORGANIZATION_CUSTOMER
  );
  const [isThresholdsDirty, setIsThresholdsDirty] = React.useState(false);
  const [isTerminationPeriodsDirty, setIsTerminationPeriodsDirty] =
    React.useState(false);
  const [isCustomerDirty, setIsCustomerDirty] = React.useState(false);

  const { t } = useIBTranslation();

  const { data: customersData, loading: loadingCustomers } = useQuery(
    GET_ALL_CUSTOMERS,
    {
      fetchPolicy: 'no-cache'
    }
  );

  React.useEffect(() => {
    onDirtyChange?.(false);
  }, [onDirtyChange]);

  const [thresholds, dispatch] = React.useReducer(
    (
      state: Record<ThresholdKey, NumericInputValue>,
      data: Partial<{ [key in keyof typeof state]: NumericInputValue }>
    ) => ({
      ...state,
      ...data
    }),
    {
      facility: 0,
      project: 0,
      tender: 0
    }
  );

  const [terminationPeriods, updateTerminationPeriods] = React.useReducer(
    (
      state: Record<TerminationPeriodKey, NumericInputValue>,
      data: Partial<{ [key in keyof typeof state]: NumericInputValue }>
    ) => ({
      ...state,
      ...data
    }),
    {
      facility: DEFAULT_TERMINATION_PERIOD,
      project: DEFAULT_TERMINATION_PERIOD
    }
  );

  const [selectedCustomer, setSelectedCustomer] = React.useState<{
    id: string;
    name: string;
  } | null>(null);

  const [revealedTerminationPeriods, setRevealedTerminationPeriods] =
    React.useState<Record<TerminationPeriodKey, boolean>>({
      facility: false,
      project: false
    });

  React.useEffect(() => {
    if (organizationData?.customer) {
      setSelectedCustomer(organizationData.customer);
    } else {
      setSelectedCustomer(null);
    }
  }, [organizationData?.customer]);

  const handleSave = async () => {
    let thresholdsSuccess = false;
    let terminationPeriodsSuccess = false;
    let customerSuccess = false;

    if (isThresholdsDirty) {
      try {
        await updateOrganizationThresholds({
          variables: {
            id: organizationData.id,
            body: {
              FacilityThreshold: thresholds.facility,
              ProjectThreshold: thresholds.project,
              TenderThreshold: thresholds.tender
            }
          }
        });
        thresholdsSuccess = true;
        await refetchOrgSettings();
      } catch (error) {
        console.error('Failed to save thresholds:', error);
      }
    } else {
      thresholdsSuccess = true;
    }

    if (isTerminationPeriodsDirty) {
      try {
        await updateOrganizationTerminationPeriods({
          variables: {
            id: organizationData.id,
            body: {
              FacilityTerminationPeriod: normalizeTerminationPeriod(
                terminationPeriods.facility
              ),
              ProjectTerminationPeriod: normalizeTerminationPeriod(
                terminationPeriods.project
              )
            }
          }
        });
        terminationPeriodsSuccess = true;
        await refetchOrgSettings();
      } catch (error) {
        console.error('Failed to save termination periods:', error);
      }
    } else {
      terminationPeriodsSuccess = true;
    }

    if (isCustomerDirty) {
      try {
        await updateOrganizationCustomer({
          variables: {
            id: organizationData.id,
            body: {
              ...organizationData,
              customer: selectedCustomer
            }
          }
        });
        customerSuccess = true;
      } catch (error) {
        console.error('Failed to save customer:', error);
      }
    } else {
      customerSuccess = true;
    }

    if (thresholdsSuccess) {
      setIsThresholdsDirty(false);
    }
    if (terminationPeriodsSuccess) {
      setIsTerminationPeriodsDirty(false);
    }
    if (customerSuccess) {
      setIsCustomerDirty(false);
    }

    if (thresholdsSuccess && terminationPeriodsSuccess && customerSuccess) {
      onDirtyChange?.(false);
    }
  };

  React.useEffect(() => {
    const thresholdSettings = data.settings.reduce((acc, setting) => {
      if (
        setting.key === 'facility_threshold' ||
        setting.key === 'project_threshold' ||
        setting.key === 'tender_threshold'
      ) {
        acc[setting.key] = setting.value;
      }
      return acc;
    }, {});

    // Calculate next values from API and only apply if they differ and form is not dirty
    const next = {
      facility: Number(thresholdSettings.facility_threshold) || 0,
      project: Number(thresholdSettings.project_threshold) || 0,
      tender: Number(thresholdSettings.tender_threshold) || 0
    };

    const changed =
      next.facility !== thresholds.facility ||
      next.project !== thresholds.project ||
      next.tender !== thresholds.tender;

    if (!isThresholdsDirty && changed) {
      dispatch(next);
    }
  }, [
    data.settings,
    thresholds.facility,
    thresholds.project,
    thresholds.tender,
    isThresholdsDirty
  ]);

  React.useEffect(() => {
    const terminationPeriodSettings = data.settings.reduce((acc, setting) => {
      if (
        setting.key === 'facility_termination_period' ||
        setting.key === 'project_termination_period'
      ) {
        acc[setting.key] = setting.value;
      }
      return acc;
    }, {});

    const next = {
      facility: normalizeTerminationPeriod(
        terminationPeriodSettings.facility_termination_period
      ),
      project: normalizeTerminationPeriod(
        terminationPeriodSettings.project_termination_period
      )
    };

    const changed =
      next.facility !== terminationPeriods.facility ||
      next.project !== terminationPeriods.project;

    if (!isTerminationPeriodsDirty && changed) {
      updateTerminationPeriods(next);
    }
  }, [
    data.settings,
    terminationPeriods.facility,
    terminationPeriods.project,
    isTerminationPeriodsDirty
  ]);

  const hasTerminationPeriodSetting = (key: TerminationPeriodKey) => {
    const setting = data.settings.find(
      (s) => s.key === `${key}_termination_period`
    );
    return setting != null && setting.value != null && setting.value !== '';
  };

  const { total, projects, facilities, selections, tenders, loading } =
    useOrganizationBindersUsage(organizationData?.id, t);

  const convertBytesToMB = (bytes) => {
    if (bytes === null || bytes === undefined || bytes === 0) {
      return `0 MB`;
    }
    if (bytes < 1024 * 1024) {
      return `< 1 MB`;
    } else {
      return `${Math.round(bytes / 1024 / 1024).toLocaleString()} MB`;
    }
  };

  const getSummaryValue = (key, convertToMB = false) => {
    const value = organizationSummaryData?.organizationSummary?.[key];
    if (convertToMB) {
      return convertBytesToMB(value);
    }
    return value?.toLocaleString() || '0';
  };

  const summaryData = {
    totalSize: getSummaryValue('totalSizeInBytes', true),
    totalDeletedSize: getSummaryValue('totalDeletedSizeInBytes', true),
    deletedDocumentsCount: getSummaryValue('deletedDocumentsCount'),
    deletedVersionsCount: getSummaryValue('deletedVersionsCount'),
    documentVersionsCount: getSummaryValue('documentVersionsCount'),
    documentsCount: getSummaryValue('documentsCount')
  };

  const infoString = (resource) => (state) =>
    `${resource[state]} ${t(`state.${state}`)}`;

  const handleCustomerChange = (index: number) => {
    const customers = customersData?.get || [];
    const customer = customers[index];
    if (customer) {
      setSelectedCustomer({ id: customer.id, name: customer.name });
      setIsCustomerDirty(true);
      onDirtyChange?.(true);
    }
  };

  const renderThresholdInput = (key: ThresholdKey) => {
    if (!useSelectOrgPackage) {
      return (
        <OrgPropertyDisplay label={t('maxActive')} value={thresholds[key]} />
      );
    }
    return (
      <>
        {key !== 'tender' &&
          (hasTerminationPeriodSetting(key) ||
          revealedTerminationPeriods[key] ? (
            <ThemeTextField
              inputMode="numeric"
              label={t('setTerminationPeriod')}
              loading={loadingOrgSettings}
              max={12}
              min={1}
              onBlur={(e) => {
                updateTerminationPeriods({
                  [key]: normalizeTerminationPeriod(
                    isNaN(e.currentTarget.valueAsNumber)
                      ? ''
                      : e.currentTarget.valueAsNumber
                  )
                });
              }}
              onChange={(e) => {
                setIsTerminationPeriodsDirty(true);
                onDirtyChange?.(true);
                const value = e.currentTarget.valueAsNumber;
                updateTerminationPeriods({
                  [key]: isNaN(value) ? '' : Math.floor(value)
                });
              }}
              step="1"
              type="number"
              value={terminationPeriods[key].toString()}
            />
          ) : (
            <ThemeButton
              buttonType={buttonTypes.DefaultDestructive}
              noPadding
              onClick={() => {
                setRevealedTerminationPeriods({
                  facility: true,
                  project: true
                });
                setIsTerminationPeriodsDirty(true);
                onDirtyChange?.(true);
              }}
            >
              {t('terminationPeriodNotSet')}
            </ThemeButton>
          ))}
        <ThemeTextField
          inputMode="numeric"
          label={t('maxActive')}
          loading={loadingOrgSettings}
          min={0}
          onBlur={(e) => {
            const value = e.currentTarget.valueAsNumber;
            // On blur, ensure we have a valid non-negative integer
            dispatch({
              [key]: Math.max(0, isNaN(value) ? 0 : Math.floor(value))
            });
          }}
          onChange={(e) => {
            setIsThresholdsDirty(true);
            onDirtyChange?.(true);
            const value = e.currentTarget.valueAsNumber;
            dispatch({
              // Set as empty string if NaN, will be set to 0 on blur
              [key]: isNaN(value) ? '' : Math.floor(value)
            });
          }}
          step="1"
          type="number"
          value={thresholds[key].toString()} // toString() removes leading 0
        />
      </>
    );
  };

  return (
    <StyledFlexbox flexDirection="column">
      <Flexbox>
        <OrgPropertyDisplay
          loading={!organizationData}
          label={t('organizationId')}
          showCopy={true}
          value={organizationData?.id}
        />
      </Flexbox>
      <Row>
        <Column>
          <OrgPropertyDisplay
            loading={!organizationData}
            label={t('marketId')}
            showCopy={true}
            value={organizationData?.market?.id}
          />
        </Column>
        <Column>
          <OrgPropertyDisplay
            loading={!organizationData}
            label={t('market')}
            value={
              <FlagFlexbox alignItems="center">
                <GetFlag countryName={organizationData?.market?.name} />
                <span>{organizationData?.market?.name}</span>
              </FlagFlexbox>
            }
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <OrgPropertyDisplay
            loading={!organizationData}
            label={t('createdDate')}
            value={getFormatedDate(organizationData?.created)}
          />
        </Column>
        <Column>
          <OrgPropertyDisplay
            loading={!organizationData}
            label={t('platform')}
            value={
              platformData.find((p) =>
                platform && platform !== null
                  ? p.Key === platform
                  : p.Key === DEFAULT_PLATFORM
              )?.Value
            }
          />
        </Column>
      </Row>
      <Row>
        <Column>
          <CustomerSelectorContainer>
            <SearchableSingleSelect
              activeKey={
                selectedCustomer
                  ? customersData?.get?.findIndex(
                      (c) => c.id === selectedCustomer.id
                    )
                  : null
              }
              changeActiveItem={handleCustomerChange}
              data={
                customersData?.get
                  ? customersData.get.map((customer, i) => ({
                      Value: customer.name,
                      Key: i
                    }))
                  : []
              }
              loading={loadingCustomers}
              label="Customer"
              dropdownText="No customer set"
              searchPlaceholderText="Search"
            />
          </CustomerSelectorContainer>
        </Column>
        <Column>
          {selectedCustomer && (
            <OrgPropertyDisplay
              loading={false}
              label="Customer ID"
              showCopy={true}
              value={selectedCustomer.id}
            />
          )}
        </Column>
      </Row>
      <PackageHeader>{t('organizationSummary')}*</PackageHeader>
      {loadingOrganizationSummary ? (
        <Card loading={true} />
      ) : (
        SUMMARY_LAYOUT.map((rowData, rowIndex) => (
          <Row key={`summary-row-${rowIndex}`}>
            {rowData.map((key, colIndex) => (
              <Column key={`summary-col-${rowIndex}-${colIndex}`}>
                <OrgPropertyDisplay label={t(key)} value={summaryData[key]} />
              </Column>
            ))}
          </Row>
        ))
      )}
      <Row>
        <TextElement fontStyle="extraExtraSmallFont">
          *{t('filesExcludedDisclaimer')}
        </TextElement>
      </Row>
      <PackageHeader>
        {upperFirst(t('binders'))} {total}
      </PackageHeader>
      {loading ? (
        <Card loading={true} />
      ) : (
        <>
          <BinderRow>
            <BinderUsageDisplay
              title={`${t('projectBinders')} ${projects.totalCount}`}
              info={['activeCount', 'archivedCount', 'warrantyCount']
                .map(infoString(projects))
                .join(', ')}
            />
            {renderThresholdInput('project')}
          </BinderRow>
          <BinderRow>
            <BinderUsageDisplay
              title={`${t('facilityBinders')} ${facilities.totalCount}`}
              info={['activeCount', 'archivedCount', 'warrantyCount']
                .map(infoString(facilities))
                .join(', ')}
            />
            {renderThresholdInput('facility')}
          </BinderRow>
          <BinderRow>
            <BinderUsageDisplay
              title={`${t('etenderingBinders')} ${tenders.totalCount}`}
              info={['activeCount', 'archivedCount', 'endedCount']
                .map(infoString(tenders))
                .join(', ')}
            />
            {renderThresholdInput('tender')}
          </BinderRow>
          <Row>
            <Column>
              <BinderUsageDisplay
                title={`${t('selectionBinders')} ${selections.totalCount}`}
              />
            </Column>
            <Column>
              {(isThresholdsDirty ||
                isTerminationPeriodsDirty ||
                isCustomerDirty) && (
                <Flexbox justifyContent="flex-end">
                  <ThemeButton
                    buttonType={buttonTypes.Primary}
                    onClick={handleSave}
                  >
                    {t('save')}
                  </ThemeButton>
                </Flexbox>
              )}
            </Column>
          </Row>
        </>
      )}
    </StyledFlexbox>
  );
};
export default OrganizationInfo;
